import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, Layout, Modal, Space, Table, Tag, Typography } from 'antd';
import { ArrowLeftOutlined, EyeOutlined, LogoutOutlined } from '@ant-design/icons';

import BatchResultDisplay from './BatchResultDisplay';
import { clearAuth } from '../auth';
import { API_BASE } from '../App';

const { Header, Content } = Layout;
const { Text }            = Typography;

const FORM_COLOURS = {
  obwt:            'blue',
  duracan_general: 'green',
  psp:             'purple',
};

export default function ResultPage() {
  const navigate              = useNavigate();
  const location              = useLocation();
  const [modalOpen, setModalOpen] = useState(false);

  const result = location.state?.result;

  function handleLogout() {
    clearAuth(); // clears sessionStorage + hard redirects to /coc_extract/login
  }

  function handleScanAnother() {
    navigate('/coc_ui', { replace: true });
  }

  if (!result) {
    navigate('/coc_ui', { replace: true });
    return null;
  }

  const uploads = (result.results || [])
    .filter(r => r.success)
    .map((r, i) => ({
      key:             i,
      formLabel:       r.formLabel || r.formType || '—',
      formType:        r.formType,
      referenceNumber: r.referenceNumber,
      filename:        r.filename || '—',
    }));

  const columns = [
    {
      title: '#', width: 36,
      render: (_, __, i) => <Text type="secondary" style={{ fontSize: 12 }}>{i + 1}</Text>,
    },
    {
      title: 'Form Type', dataIndex: 'formLabel',
      render: (label, row) => (
        <Tag color={FORM_COLOURS[row.formType] || 'default'} style={{ fontSize: 11 }}>
          {label}
        </Tag>
      ),
    },
    {
      title: 'Reference No.', dataIndex: 'referenceNumber',
      render: val => <Text strong style={{ fontSize: 13 }}>{val}</Text>,
    },
    {
      title: 'Saved As', dataIndex: 'filename',
      render: val => (
        <Text style={{ fontSize: 11, color: '#595959', wordBreak: 'break-all' }}>{val}</Text>
      ),
    },
  ];

  return (
    <Layout style={{ minHeight: '100vh', background: '#f5f5f5' }}>

      <Header style={{
        position: 'sticky', top: 0, zIndex: 100,
        background: '#fff', borderBottom: '1px solid #e8e8e8',
        padding: '0 16px', height: 52,
        display: 'flex', alignItems: 'center', gap: 10,
      }}>
        <Button type="text" icon={<ArrowLeftOutlined />} onClick={handleScanAnother}
          style={{ padding: '0 4px' }} title="Back to scan" />
        <Text strong style={{ fontSize: 15, letterSpacing: '.1em', flex: 1 }}>DURACAN</Text>
        <Text type="secondary" style={{ fontSize: 12, letterSpacing: '.08em' }}>
          EXTRACTION RESULT
        </Text>
        <Button type="text" icon={<LogoutOutlined />} onClick={handleLogout}
          style={{ color: '#8c8c8c', marginLeft: 8 }} title="Sign out" />
      </Header>

      <Content style={{ padding: '16px 12px 40px', maxWidth: 640, margin: '0 auto', width: '100%' }}>
        <Space direction="vertical" style={{ width: '100%' }} size={12}>

          <BatchResultDisplay result={result} apiBase={API_BASE} />

          {uploads.length > 0 && (
            <Button size="large" icon={<EyeOutlined />} style={{ width: '100%' }}
              onClick={() => setModalOpen(true)}>
              View Uploads ({uploads.length} form{uploads.length !== 1 ? 's' : ''})
            </Button>
          )}

          <Button size="large" style={{ width: '100%' }} onClick={handleScanAnother}>
            ← Scan Another Batch
          </Button>

        </Space>
      </Content>

      <Modal
        title={
          <Space>
            <EyeOutlined />
            <span>Uploads This Session</span>
            <Tag>{uploads.length} form{uploads.length !== 1 ? 's' : ''}</Tag>
          </Space>
        }
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        footer={<Button type="primary" onClick={() => setModalOpen(false)}>Close</Button>}
        width="92vw"
        style={{ maxWidth: 560, top: 40 }}
        styles={{ body: { padding: '12px 0' } }}
      >
        <Table dataSource={uploads} columns={columns}
          pagination={false} size="small" bordered scroll={{ x: true }} />
      </Modal>

    </Layout>
  );
}